import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { assetCatalog, type CatalogVersionDetail, type CatalogVersionRow } from "@/api/client";
import { CAP, useSession } from "@/auth/session";
import { useCompanies } from "@/modules/registry/parts_common";
import { DataTable, type DataCol } from "@/components/DataTable";
import { Drawer } from "@/components/Drawer";
import { Modal } from "@/components/Modal";
import { MultiSelect } from "@/components/MultiSelect";
import { useMutationFeedback } from "@/lib/hooks/useMutationFeedback";
import { KV } from "@/components/KV";
import { PageHead } from "@/components/PageHead";
import { StatGrid } from "@/components/StatGrid";
import { Select, SearchInput } from "@/components/inputs";
import { ago, dateTime, POLL } from "@/lib/format";
import {
  KindSelect, MODEL_CHIP, ModelChip, QK, SpecsView, Thumb, kindLabel, useManufacturers, useTemplates,
} from "@/modules/asset_catalog/common";

/** Which action page edits a kind. */
export function editPath(kind: string, asset: boolean | undefined) {
  if (kind === "battery") return "/asset-catalog/batteries";
  if (kind === "vehicle") return "/asset-catalog/vehicles";
  return asset ? "/asset-catalog/vehicles" : "/asset-catalog/subcomponents";
}

/** Overview = the lookup surface: every model version across kinds, with
 *  filters, and a READ drawer. Mutations live on the action pages. */
export function AssetCatalogOverviewPage() {
  const nav = useNavigate();
  const tpl = useTemplates().data;
  const mfrs = useManufacturers().data?.manufacturers ?? [];
  // Only a platform operator authors the catalogue, so only they see the
  // visibility column and the scope editor (MANAGE_CATALOG, internal-only).
  const qc = useQueryClient();
  const { can } = useSession();
  const canManage = can(CAP.MANAGE_CATALOG);
  const companies = useCompanies().data?.companies ?? [];
  const companyName = useMemo(() => {
    const m = new Map(companies.map((c) => [c.company_id, c.name]));
    return (id: string) => m.get(id) ?? id;
  }, [companies]);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [mfr, setMfr] = useState("");
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState<string | null>(null);

  const stats = useQuery({ queryKey: [...QK, "stats"], queryFn: assetCatalog.stats, refetchInterval: POLL.LIST });
  const q = useQuery({
    queryKey: [...QK, "models", kind, status, mfr, search],
    queryFn: () => assetCatalog.models({
      kind: kind || undefined, status: status || undefined, manufacturer_id: mfr || undefined,
      search: search || undefined, limit: "1000",
    }),
    refetchInterval: POLL.LIST,
  });
  const rows = q.data?.models ?? [];
  const detail = useQuery({ queryKey: [...QK, "detail", sel], queryFn: () => assetCatalog.model(sel!), enabled: !!sel });

  const dim = <span style={{ color: "var(--t-dim)" }}>—</span>;
  const cols = useMemo<DataCol<CatalogVersionRow>[]>(() => [
    { key: "pic", label: "", width: 44, render: (m) => <Thumb src={m.image ? assetCatalog.imageUrl(m.version_id, m.image.captured_at) : null} /> },
    { key: "kind", label: "Kind", width: 150, render: (m) => <>{kindLabel(tpl, m.kind)} <span className="mono" style={{ color: "var(--t-mute)", fontSize: 11 }}>{tpl?.kinds.find((k) => k.id === m.kind)?.abbr ?? ""}</span></> },
    { key: "mfr", label: "Manufacturer", width: 140, render: (m) => m.manufacturer },
    { key: "name", label: "Model", width: 160, render: (m) => <span style={{ fontWeight: 600 }}>{m.name}</span> },
    { key: "ver", label: "Ver", width: 56, mono: true, render: (m) => `v${m.version}` },
    { key: "status", label: "Status", width: 92, render: (m) => <ModelChip st={m.status} /> },
    { key: "specs", label: "Key specs", width: 320, render: (m) => m.key_specs || dim, title: (m) => m.key_specs },
    { key: "units", label: "Units", width: 70, mono: true, render: (m) => (m.units == null ? dim : m.units) },
    { key: "upd", label: "Updated", width: 90, mono: true, render: (m) => ago(m.updated_at), title: (m) => dateTime(m.updated_at) },
    // Only a platform operator can see more than one company's worth of
    // catalogue, so only they have a use for this column — for everyone else
    // every row would read "All companies", which is noise.
    ...(canManage ? [{
      key: "vis", label: "Visible to", width: 150,
      render: (m: CatalogVersionRow) => m.companies.length
        ? <span title={m.companies.map(companyName).join(", ")}>
            {m.companies.length === 1 ? companyName(m.companies[0])
                                      : `${m.companies.length} companies`}
          </span>
        : <span style={{ color: "var(--t-dim)" }}>All companies</span>,
    } as DataCol<CatalogVersionRow>] : []),
  ], [tpl, canManage, companyName]);

  const st = stats.data;
  const d = detail.data;
  const [scopeFor, setScopeFor] = useState<CatalogVersionDetail | null>(null);
  const dKind = d ? tpl?.kinds.find((k) => k.id === d.kind) : undefined;

  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Catalog · Overview" title="Models"
        desc="What a vehicle or battery IS: model versions with their specs and bill of materials. Units in the registry point at a version; edits save into that version in place." />

      <StatGrid stats={[
        { k: "Asset models", v: st?.asset_models ?? "—", s: "vehicle + battery versions" },
        { k: "Subcomponent models", v: st?.subcomponent_models ?? "—" },
        { k: "Drafts", v: st?.drafts ?? "—", s: "not yet buildable" },
        { k: "Units on a model", v: st ? `${st.units_on_model} / ${st.units_total}` : "—", s: "registry assets with model set" },
      ]} />

      <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <SearchInput placeholder="Search model / manufacturer…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
        <KindSelect kinds={tpl?.kinds ?? []} value={kind} onChange={setKind} placeholder="All kinds" />
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.keys(MODEL_CHIP).map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
        <Select value={mfr} onChange={(e) => setMfr(e.target.value)}>
          <option value="">All manufacturers</option>
          {mfrs.map((m) => <option key={m.manufacturer_id} value={m.manufacturer_id}>{m.name}</option>)}
        </Select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--t-mute)", alignSelf: "center" }}>
          {q.data ? `${q.data.total} version${q.data.total === 1 ? "" : "s"}` : ""}
        </span>
      </div>
      <DataTable rows={rows} cols={cols} rowKey={(r) => r.version_id} pageSize={100}
        onRowClick={(r) => setSel(r.version_id)}
        empty={<div className="panel panel-pad cap">
          No models match — add manufacturers first, then subcomponents, then batteries and vehicles.
        </div>} />

      {sel && d && (
        <Drawer onClose={() => setSel(null)} width="min(640px,96vw)"
          header={<>
            <span style={{ fontWeight: 700 }}>{d.name}</span>
            <span className="mono cap">v{d.version}</span>
            <ModelChip st={d.status} />
            <button className="btn ghost sm" style={{ marginLeft: 8 }}
              onClick={() => nav(`${editPath(d.kind, dKind?.asset)}?id=${d.version_id}`)}>✎ Edit</button>
            {canManage && (
              <button className="btn ghost sm" onClick={() => setScopeFor(d)}
                title="Which companies may build with this model">
                {d.companies.length ? `Visible to ${d.companies.length}` : "Visible to all"}
              </button>
            )}
          </>}>
          {d.image && (
            <img src={assetCatalog.imageUrl(d.version_id, d.image.captured_at)} alt={d.name}
              style={{ width: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 10,
                border: "1px solid var(--line2)", background: "#fff", marginBottom: 12 }} />
          )}
          <div className="section-label">Identity</div>
          <KV k="Kind" v={kindLabel(tpl, d.kind)} mono={false} />
          <KV k="Manufacturer" v={d.manufacturer} mono={false} />
          <KV k="Key specs" v={d.key_specs} mono={false} />
          {d.units != null && <KV k="Units on this version" v={d.units} />}
          {d.version_note && <KV k="Version note" v={d.version_note} mono={false} />}
          <KV k="Created" v={`${dateTime(d.created_at)} · ${d.created_by}`} />
          {d.activated_at && <KV k="Activated" v={dateTime(d.activated_at)} accent="var(--ok)" />}
          {d.retired_at && <KV k="Retired" v={dateTime(d.retired_at)} accent="var(--t-mute)" />}

          <SpecsView tpl={tpl} m={d} />

          {d.bom.length > 0 && <>
            <div className="section-label tight">Bill of materials</div>
            {d.bom.map((b, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--line)" }}>
                <span className="muted">{tpl?.slots[d.kind]?.find((s) => s.slot === b.slot)?.role ?? b.slot}</span>
                <span style={{ textAlign: "right" }}>
                  <a href="#" onClick={(e) => { e.preventDefault(); setSel(b.version_id); }} style={{ fontWeight: 600 }}>
                    {b.name} <span className="mono muted">v{b.version}</span>
                  </a>
                  {b.qty > 1 && <span className="mono"> ×{b.qty}</span>}
                  <span className="muted"> · {b.manufacturer}</span>
                  {b.status !== "active" && <> <ModelChip st={b.status} /></>}
                </span>
              </div>
            ))}
          </>}

          <div className="section-label tight">Versions of this line</div>
          {d.versions.map((v) => (
            <div key={v.version_id} style={{ display: "flex", gap: 10, fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
              <a href="#" className="mono" onClick={(e) => { e.preventDefault(); setSel(v.version_id); }}
                style={{ fontWeight: v.version_id === d.version_id ? 700 : 500 }}>v{v.version}</a>
              <ModelChip st={v.status} />
              <span className="muted">{v.version_note ?? ""}</span>
            </div>
          ))}

          {d.used_in && <>
            <div className="section-label tight">Used in</div>
            {d.used_in.length === 0 && <div className="cap">Not in any bill of materials yet.</div>}
            {d.used_in.map((u) => (
              <div key={u.version_id} style={{ display: "flex", gap: 10, fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
                <a href="#" onClick={(e) => { e.preventDefault(); setSel(u.version_id); }} style={{ fontWeight: 600 }}>
                  {u.name} <span className="mono muted">v{u.version}</span>
                </a>
                <span className="muted">{kindLabel(tpl, u.kind)}</span>
                <ModelChip st={u.status} />
              </div>
            ))}
          </>}
        </Drawer>
      )}
      {scopeFor && (
        <ModelScopeModal model={scopeFor} companies={companies}
          onClose={() => setScopeFor(null)}
          onSaved={() => { setScopeFor(null); void qc.invalidateQueries({ queryKey: QK }); }} />
      )}
    </div>
  );
}


/** Which companies may BUILD WITH a catalog model (D-81).
 *
 *  Deliberately the same control, wording and shape as FOTA's release scope
 *  (EditScopeModal in FirmwarePage): one idea in one form, so an operator who
 *  has set a firmware scope already knows how to set a catalogue one.
 *
 *  It governs pickers and enrolment, NOT reading — a company with assets
 *  already on this model keeps seeing it on them, because tightening a list
 *  must never blank out the name of equipment somebody already owns.
 */
function ModelScopeModal({ model, companies, onClose, onSaved }: {
  model: CatalogVersionDetail;
  companies: { company_id: string; name: string }[];
  onClose: () => void; onSaved: () => void;
}) {
  const fb = useMutationFeedback();
  const [ids, setIds] = useState<string[]>(model.companies);
  const save = useMutation({
    mutationFn: () => assetCatalog.setModelCompanies(model.version_id, ids),
    onSuccess: () => { fb.ok(); onSaved(); },
    onError: fb.fail,
  });
  return (
    <Modal title={`Visible to — ${model.name} v${model.version}`} onClose={onClose}
      footer={<>
        {fb.errorNode}
        <button className="btn ghost sm" onClick={onClose}>Cancel</button>
        <button className="btn teal sm" disabled={save.isPending} onClick={() => save.mutate()}>Save</button>
      </>}>
      <div style={{ fontSize: 12.5, color: "var(--t-mute)", marginBottom: 12 }}>
        Companies that may register assets and subcomponents against this model.
        Empty = <b>every</b> company. Companies that already own something built
        on it keep seeing it on their own equipment either way.
      </div>
      <MultiSelect options={companies.map((c) => ({ value: c.company_id, label: c.name }))}
        value={ids} onChange={setIds} emptyLabel="All companies" clearLabel="Clear — all companies" />
    </Modal>
  );
}
